import {
  errorIsStatus,
  fetcher,
  useApplicantAPI,
  usePollingBackoff,
  useRequest,
} from "@certn/api";
import { components } from "@certn/api/generated-schemas/applicant-portal";
import { ThemeContext } from "@certn/cascadia";
import {
  Text,
  Link,
  Stack,
  Illustration,
  ButtonGroup,
  GhostButton,
  Space,
  PrimaryButton,
  BasicButton,
  SectionMessage,
  showToast,
  ModalTrigger,
  Modal,
} from "@certn/cascadia-react";
import { OneIdImage } from "@certn/cascadia-react/foundations/illustrations/OneIdImage";
import { getPrivacyPolicyUrl, getRegionKey } from "@certn/utils";
import { css } from "@emotion/css";
import { UISchemaElement } from "@jsonforms/core";
import * as Sentry from "@sentry/react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation, Trans } from "react-i18next";

import { ConfirmCloseTrustmaticModal } from "./ConfirmCloseOneIdIFrameModal";
import { DemoSkipModal } from "./DemoSkipModal";
import { NfcIdVerificationFlow } from "./NfcIdVerificationFlow";
import { OneIDLoader } from "./OneIDLoader";
import { OneIdIFrameModal } from "./OneIdIFrameModal";
import { SkipBiometricConsent } from "./SkipBiometricConsent";
import { useBiometricIdOptOutHandler } from "./hooks/useBiometricIdOptOutHandler";
import { useFallbackHandler } from "./hooks/useFallbackHandler";
import { useApplication } from "../../ApplicationContext";
import { useApplicationFormContext } from "../../ApplicationFormContext";
import Constants from "../../constants";
import { OneIdLayoutContext } from "../../layouts/OneIdLayoutContext";
import { getAllowRealResponses } from "../../utils";
import {
  BiometricConsentModal,
  useBiometricConsentStatus,
} from "./BiometricConsentModal";
import { BiometricNoticeModal } from "./BiometricNoticeModal";
import { isOneId1SectionComplete } from "./isOneId1SectionComplete";
import { ONE_ID_COMPLETION_TIMESTAMP_KEY } from "../constants";

type CreateSessionData = {
  src_url: string;
  one_id_session_id: string;
};

export type TrustmaticEventNotification = {
  hasCompleted: boolean;
  hasError: boolean;
  sessionId: string;
  error?: string;
};

type CheckStatus = components["schemas"]["CheckStatus"];
type OneIdCheckStatusResponse =
  components["schemas"]["OneIDCheckStatusResponse"];
type OneIDFallbackEligibilityResponse =
  components["schemas"]["OneIDFallbackEligibilityResponse"];

// Statuses that mean the OneID check is no longer progressing. Used to stop
// polling once the check has reached a terminal state; without this, polling
// would continue indefinitely for non-COMPLETE terminal states (CANCELLED,
// IN_DISPUTE, SUPERSEDED) since `startPollingOneIdStatus` is never reset.
const TERMINAL_CHECK_STATUSES: CheckStatus[] = [
  "CANCELLED",
  "COMPLETE",
  "IN_DISPUTE",
  "SUPERSEDED",
];

const isOneIdCheckTerminal = (checkStatus?: CheckStatus) =>
  !!checkStatus && TERMINAL_CHECK_STATUSES.includes(checkStatus);

const SESSION_TERMINATED_BY_ERRORS = "SESSION_TERMINATED_BY_ERRORS";

interface Props {
  data: string;
  uischema: UISchemaElement;
  fulfillWithDemoResponses: {
    only_fulfill_with_demo_responses: boolean;
    check_types_allowing_non_demo_responses: string[];
  };
  consumer?: boolean;
}

export function BiometricIdSection(props: Props) {
  const { t, i18n } = useTranslation();
  const { caseId } = useApplicationFormContext();

  const {
    label,
    subLabel,
    setShowNonBiometricIdSection,
    handleChange,
    fallbackPollTimeout,
    optedOutOfBiometricId,
    oneIdAlternativesEligibility,
    idvCheckTypes,
    showNonBiometricIdSection,
  } = useContext(OneIdLayoutContext);

  const allowRealOneIdResponses = getAllowRealResponses(
    props.fulfillWithDemoResponses,
    ["ONE_ID_1", "ONE_ID_2"]
  );

  const { uischema } = props;
  const options = uischema.options || {};

  const language = i18n.language.split("-")[0];
  const privacyPolicyUrl = getPrivacyPolicyUrl(language);
  const region = getRegionKey();
  const contractId =
    language === "fr"
      ? options?.french_consent_id
      : region === "au"
        ? props.consumer
          ? options?.australian_consumer_consent_id
          : options?.australian_consent_id
        : options?.english_consent_id;

  const request = useRequest();
  const {
    applicantPortalLogTrustmaticResponseTimes,
    enableOneIdOptOut,
    applicantPortalDisableOneIdOptOutOnceSessionStarted,
    applicantPortalBiometricConsentModal,
  } = useFlags();

  const { onlyFulfillWithDemoResponses, navigateToSection, prevSection } =
    useApplication();

  const [isTrustmaticUrlLoading, setIsTrustmaticUrlLoading] = useState(true);
  const [trustmaticUrlError, setTrustmaticUrlError] = useState(false);
  const [trustmaticUrl, setTrustmaticUrl] = useState("");
  const [oneIdSessionId, setOneIdSessionId] = useState<string>(props.data);
  const [showIFrame, setShowIFrame] = useState(false);
  const [trustmaticResponse, setTrustmaticResponse] = useState<
    TrustmaticEventNotification | undefined
  >(undefined);
  const [openConfirmCloseTrustmaticModal, setOpenConfirmCloseTrustmaticModal] =
    useState(false);
  const [showDemoSkipModal, setShowDemoSkipModal] = useState(false);
  const [waitForDemoResponse, setWaitForDemoResponse] = useState(false);
  // ONE_ID_1 demo skip only enqueues the mocked webhook. Hold the section on
  // the loader until one-id-check-status actually completes (CHCKS-1599).
  const [awaitingDemoCompletion, setAwaitingDemoCompletion] = useState(false);
  // The dummy session id is written 500ms after confirm. Keep the loader up
  // until that write so Next cannot submit without the required uuid.
  const [awaitingDemoSessionWrite, setAwaitingDemoSessionWrite] =
    useState(false);
  const [showNfcFlow, setShowNfcFlow] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentedThisSession, setConsentedThisSession] = useState(false);
  const nfcFlowEnabled = options?.nfc_flow_enabled === true;
  const { theme } = useContext(ThemeContext);
  const { callFallbackTimeout } = useFallbackHandler();
  const [startPollingOneIdStatus, setStartPollingOneIdStatus] = useState(false);
  const {
    hasConsentedToCurrentVersion,
    superdocEnabled,
    superdocEsignAvailable,
  } = useBiometricConsentStatus(contractId);
  const skipConsentModal = hasConsentedToCurrentVersion || consentedThisSession;

  // Refs holding the latest render values for stop predicates passed to
  // `usePollingBackoff`. SWR holds the returned refreshInterval callback for
  // the lifetime of the cache key, so the predicates need refs (not closures)
  // to see fresh values on each interval tick. See FR-141.
  const propsDataRef = useRef(props.data);
  propsDataRef.current = props.data;
  const hasOneIdV1 = idvCheckTypes?.includes("ONE_ID_1");
  const hasOneIdV1Ref = useRef(hasOneIdV1);
  hasOneIdV1Ref.current = hasOneIdV1;
  const waitForDemoResponseRef = useRef(waitForDemoResponse);
  waitForDemoResponseRef.current = waitForDemoResponse;
  const eligibilityErrorRef = useRef<unknown>(undefined);

  // FE-8.B: surface a Sentry signal when `usePollingBackoff`'s 5 min
  // wall-clock budget is exhausted. The hook fires this once per polling
  // session (re-armed on a `shouldStop=true` transition), so we don't need
  // additional dedupe gating here. Distinct `caller` tags so the two sites
  // are searchable independently in Sentry.
  const reportCheckStatusBudgetExhausted = useCallback(() => {
    Sentry.captureMessage("polling-budget-exhausted", {
      level: "info",
      tags: {
        hook: "usePollingBackoff",
        caller: "BiometricIdSection.check-status",
      },
    });
  }, []);
  const reportEligibilityBudgetExhausted = useCallback(() => {
    Sentry.captureMessage("polling-budget-exhausted", {
      level: "info",
      tags: {
        hook: "usePollingBackoff",
        caller: "BiometricIdSection.eligibility",
      },
    });
  }, []);

  // Poll until the OneID check reaches any terminal state so the
  // post-completion `disableOptOut` safety valve below is actually reachable.
  // We cannot gate on `=== "AWAITING_APPLICANT_SUBMISSION"` because the poll
  // would stop as soon as the status transitioned to IN_PROGRESS (before ever
  // seeing COMPLETE). We also cannot gate on `!== "COMPLETE"` alone: if the
  // check landed in any other terminal state (CANCELLED, IN_DISPUTE,
  // SUPERSEDED) polling would continue forever since `startPollingOneIdStatus`
  // is never reset to false.
  // The predicate only handles the IN-CYCLE stop (terminal status). The
  // START conditions (`applicantPortalDisableOneIdOptOutOnceSessionStarted`
  // + `startPollingOneIdStatus`, a ONE_ID_1 demo skip waiting on the
  // mocked webhook, or an in-session Trustmatic termination) are
  // enforced at the SWR config level below — when `shouldStop` returns
  // `true` on the FIRST call, SWR has no later trigger to re-evaluate
  // `refreshInterval` (the function ref is stable, and the user clicking
  // "Start Verification" doesn't cause a fetch). Gating the SWR config
  // object on the start booleans makes SWR see a config change and
  // schedule the first refresh, mirroring how the original code passed
  // `{}` vs `{ refreshInterval: 1000 }`.
  // In-session only. A remount has no Trustmatic message, so the continue UI
  // below also treats a persisted CANCELLED check as termination.
  const iframeSessionTerminated =
    trustmaticResponse?.error === SESSION_TERMINATED_BY_ERRORS;

  const checkStatusRefreshInterval =
    usePollingBackoff<OneIdCheckStatusResponse>({
      shouldStop: (data) => isOneIdCheckTerminal(data?.check_status),
      onBudgetExhausted: reportCheckStatusBudgetExhausted,
    });

  const shouldPollCheckStatus =
    (applicantPortalDisableOneIdOptOutOnceSessionStarted &&
      startPollingOneIdStatus) ||
    awaitingDemoCompletion ||
    iframeSessionTerminated;

  const checkStatusSwrConfig = useMemo(
    () =>
      shouldPollCheckStatus
        ? { refreshInterval: checkStatusRefreshInterval }
        : {},
    [shouldPollCheckStatus, checkStatusRefreshInterval]
  );

  const { data: oneIdCheckStatus, mutate: refreshOneIdCheckStatus } =
    useApplicantAPI(
      `/api/internal/applicant/case/${caseId}/one-id-check-status/`,
      {},
      checkStatusSwrConfig
    );

  // FR-141: eligibility poll cadence is driven by SWR's `refreshInterval`
  // via `usePollingBackoff`. The predicate mirrors the original
  // useEffect's stop conditions (props.data present, not ONE_ID_1, no
  // error, fallback still progressing).
  //
  // Unlike `checkStatusSwrConfig`, this does NOT use a `{}`-vs-config
  // START gate — all start-like conditions live in `shouldStop`, and
  // wake-up is driven by the explicit `pollFallbackEligibility()`
  // (`mutate()`) calls in the effects below. Transient-error → resume
  // needs no dedicated effect (the fetch that clears `error` is the
  // same fetch that re-invokes `refreshInterval`); see the
  // "error transitions truthy → falsy" regression test.
  const eligibilityRefreshInterval =
    usePollingBackoff<OneIDFallbackEligibilityResponse>({
      shouldStop: (data) => {
        if (!propsDataRef.current) return true;
        if (hasOneIdV1Ref.current) return true;
        if (eligibilityErrorRef.current) return true;
        const fallback = data?.fallback;
        return !(
          fallback === "WAITING_FOR_APPLICANT" ||
          fallback === "PENDING" ||
          waitForDemoResponseRef.current
        );
      },
      onBudgetExhausted: reportEligibilityBudgetExhausted,
    });

  const eligibilitySwrConfig = useMemo(
    () => ({ refreshInterval: eligibilityRefreshInterval }),
    [eligibilityRefreshInterval]
  );

  const {
    data: fallbackEligibility,
    mutate: pollFallbackEligibility,
    error,
    isLoading: fallbackEligibilityIsLoading,
  } = useApplicantAPI(
    `/api/internal/applicant/case/${caseId}/one-id/poll-fallback-eligibility/` as "/api/internal/applicant/case/{id}/one-id/poll-fallback-eligibility/",
    {},
    eligibilitySwrConfig
  );
  eligibilityErrorRef.current = error;

  // TRU-441: Keep the non-biometric opt-out link available while a OneID
  // session is IN_PROGRESS so applicants can switch back and forth between
  // OneID and the Interac non-bio path. The LD flag now acts as a
  // post-completion safety valve (hiding the link once the check is done).
  const disableOptOut =
    applicantPortalDisableOneIdOptOutOnceSessionStarted &&
    oneIdCheckStatus?.check_status === "COMPLETE";

  const hasNotSubmittedOneIdOrOptedOut =
    fallbackEligibility?.fallback === "WAITING_FOR_APPLICANT";

  const hasSubmittedWaitingForOneIdResponse =
    fallbackEligibility?.fallback &&
    fallbackEligibility?.fallback === "PENDING";

  // FR-621: for standalone ONE_ID_1, completion must reflect the REAL check
  // status, not merely that a session id was written to applicant_form_data.
  // The previous `hasOneIdV1 && props.data` signal flipped to "submitted" as
  // soon as any session id landed (even pre-completion), leaving applicants
  // stuck on a fake success while ONE_ID_1 stayed AWAITING_APPLICANT_SUBMISSION.
  // Mirror OneIdControl (#5014): drive off `one-id-check-status` plus the
  // in-session Trustmatic "hasCompleted" signal.
  const oneIdV1Complete =
    isOneId1SectionComplete(oneIdCheckStatus) ||
    (!!trustmaticResponse?.hasCompleted && !trustmaticResponse?.hasError);

  useEffect(() => {
    if (!awaitingDemoCompletion) return;
    if (
      oneIdV1Complete ||
      isOneIdCheckTerminal(oneIdCheckStatus?.check_status)
    ) {
      setAwaitingDemoCompletion(false);
    }
  }, [awaitingDemoCompletion, oneIdV1Complete, oneIdCheckStatus?.check_status]);

  // FR-621 (follow-up): for standalone ONE_ID_1, completion must come SOLELY
  // from the real check status. The fallback-eligibility endpoint returns
  // NO_FALLBACK_REQUIRED for any case without a ONE_ID_2 (IDENTITY_VERIFICATION_1)
  // check — which is always true for standalone ONE_ID_1 — so treating that
  // value as "complete" flips the section to a fake "submitted" state while the
  // ONE_ID_1 check is still AWAITING_APPLICANT_SUBMISSION. The fallback clauses
  // only make sense for the non-biometric ONE_ID_2 flow, so gate them on
  // `!hasOneIdV1`.
  const oneIdCancelled = oneIdCheckStatus?.check_status === "CANCELLED";
  // Opt-out has no OneID check. The status endpoint then returns a synthetic
  // CANCELLED so submission can proceed; that must not become the fail path.
  const terminationSettled = oneIdCancelled && !optedOutOfBiometricId;
  const sessionTerminatedByErrors =
    iframeSessionTerminated || terminationSettled;
  const persistedTerminatedSessionId = useRef<string | undefined>(undefined);

  // ONE_ID_1 skips the session-id write on hasError (FR-621). Termination is
  // that path, so Next fails when the package requires one_id_session_id.
  // Write it once the cancelled status is known, including after remount when
  // create returns the existing terminal session.
  const { data: storedSessionId } = props;
  useEffect(() => {
    if (!terminationSettled || !oneIdSessionId) return;
    if (storedSessionId === oneIdSessionId) return;
    if (persistedTerminatedSessionId.current === oneIdSessionId) return;
    persistedTerminatedSessionId.current = oneIdSessionId;
    handleChange("one_id_session_id", oneIdSessionId);
  }, [handleChange, oneIdSessionId, storedSessionId, terminationSettled]);

  const oneIdSessionComplete = hasOneIdV1
    ? oneIdV1Complete || terminationSettled
    : Boolean(
        fallbackEligibility?.fallback === "NO_FALLBACK_REQUIRED" ||
          fallbackEligibility?.fallback === "FALLBACK_TO_SECONDARY_ID" ||
          terminationSettled
      );

  const signAgreement = async () => {
    try {
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/sign-agreement/`,
        {
          external_id: contractId,
        }
      );
    } catch (e) {
      console.error("Error signing Biometric Consent", e);
      showToast(t("Something went wrong, try again"), {
        type: "error",
      });
      Sentry.captureException(e);
      return false;
    }

    try {
      await fetcher.submit(
        `/api/internal/applicant/case/${caseId}/biometric-consent/create-claim/`
      );
    } catch (e) {
      console.error("Error creating Biometric Consent claim", e);
      Sentry.captureException(e);
      return false;
    }
    return true;
  };

  const openVerification = async () => {
    setStartPollingOneIdStatus(true);
    // In SuperDoc mode the signature + biometric claim are already recorded by
    // the agreement-signature endpoint, so skip the PactSafe sign + create-claim.
    // Gate on `superdocEsignAvailable` (esign.url present), not the raw LD flag:
    // when the backend falls back to PactSafe (flag on, no esign.url) we still
    // need sign-agreement/create-claim. After a fresh SuperDoc signature the
    // biometric-consent SWR cache may still be stale-false, but every path that
    // reaches `openVerification` with esign available has already recorded consent.
    const agreementSigned = superdocEsignAvailable
      ? true
      : await signAgreement();
    if (agreementSigned) {
      localStorage.setItem(ONE_ID_COMPLETION_TIMESTAMP_KEY, "");
      setShowIFrame(true);
    }
    return agreementSigned;
  };

  const closeTrustmaticModal = () => {
    showIFrame && setOpenConfirmCloseTrustmaticModal(true);
  };

  const returnToTrustmaticModal = () => {
    setOpenConfirmCloseTrustmaticModal(false);
  };

  const confirmCloseTrustmaticModal = () => {
    if (showIFrame) {
      setOpenConfirmCloseTrustmaticModal(false);
      setShowIFrame(false);
    }
  };

  const handleCloseIFrame = async () => {
    try {
      await request(`/api/internal/applicant/case/${caseId}/one-id/end/`, {
        method: "POST",
        body: JSON.stringify({
          one_id_session_id: oneIdSessionId,
        }),
      });
    } catch (e) {
      console.error("Error creating log for One ID session end:", e);
    }
  };

  const getTrustmaticLink = useCallback(async () => {
    // if we have a url don't fetch another one.
    if (
      trustmaticUrl ||
      optedOutOfBiometricId ||
      onlyFulfillWithDemoResponses ||
      showNonBiometricIdSection
    ) {
      setIsTrustmaticUrlLoading(false);
      return;
    }

    setIsTrustmaticUrlLoading(true);
    setTrustmaticUrlError(false);
    try {
      const { src_url, one_id_session_id } = await request<CreateSessionData>(
        `/api/internal/applicant/case/${caseId}/one-id/create/`,
        {
          method: "POST",
          body: JSON.stringify({ color_scheme: theme }),
        }
      );
      setTrustmaticUrl(src_url);
      setOneIdSessionId(one_id_session_id);
    } catch (e) {
      console.error("Error starting verification:", e);
      setTrustmaticUrlError(true);
    } finally {
      setIsTrustmaticUrlLoading(false);
    }
  }, [
    caseId,
    request,
    theme,
    trustmaticUrl,
    onlyFulfillWithDemoResponses,
    optedOutOfBiometricId,
    showNonBiometricIdSection,
  ]);

  useEffect(() => {
    if (!optedOutOfBiometricId && !hasOneIdV1) pollFallbackEligibility();
    getTrustmaticLink();
    callFallbackTimeout();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optedOutOfBiometricId]);

  const enrollmentCompleted = useCallback(
    async (event: MessageEvent) => {
      const { data }: { data: TrustmaticEventNotification } = event;
      // Helps protect against other message events interfering
      if (data.sessionId) {
        setTrustmaticResponse(data);
        // FR-621: for standalone ONE_ID_1, only persist the session id once
        // Trustmatic reports the verification actually finished. Writing it on
        // any earlier message is what previously left the OneID step showing a
        // false "submitted" state (see `oneIdV1Complete`). The non-ONE_ID_1
        // fallback flow still needs the early write to drive eligibility
        // polling, so keep that behaviour when `hasOneIdV1` is false.
        if (!hasOneIdV1 || (data.hasCompleted && !data.hasError)) {
          handleChange("one_id_session_id", oneIdSessionId);
        }
        if (hasOneIdV1 && data.hasCompleted && !data.hasError) {
          refreshOneIdCheckStatus();
        }
      }
    },
    [oneIdSessionId, handleChange, hasOneIdV1, refreshOneIdCheckStatus]
  );

  // FR-141: clears ONE_ID_COMPLETION_TIMESTAMP_KEY when fallback eligibility
  // settles into a terminal/inert state — preserves the `else if` branch
  // of the deleted `setInterval`-driven useEffect. Skip the clear while
  // polling would still be active (`hasNotSubmittedOneIdOrOptedOut &&
  // !error`) or while we're waiting on a demo / in-flight session.
  useEffect(() => {
    if (!props.data || hasOneIdV1) return;
    if (error && errorIsStatus(error, 404)) return;
    if (waitForDemoResponse) return;
    if (hasSubmittedWaitingForOneIdResponse) return;
    if (hasNotSubmittedOneIdOrOptedOut && !error) {
      return;
    }
    localStorage.setItem(ONE_ID_COMPLETION_TIMESTAMP_KEY, "");
  }, [
    props.data,
    hasOneIdV1,
    error,
    waitForDemoResponse,
    hasSubmittedWaitingForOneIdResponse,
    hasNotSubmittedOneIdOrOptedOut,
  ]);

  useEffect(() => {
    if (showIFrame) {
      window.addEventListener("message", enrollmentCompleted, false);
    } else {
      window.removeEventListener("message", enrollmentCompleted, false);
    }
  }, [enrollmentCompleted, showIFrame]);

  useEffect(() => {
    const handleTrustmaticErrorEvent = (
      _trustmaticResponse: TrustmaticEventNotification
    ) => {
      console.error("Error starting verification:", _trustmaticResponse.error);
      showToast(t("Something went wrong, try again"), { type: "error" });
    };

    if (trustmaticResponse?.error === SESSION_TERMINATED_BY_ERRORS) {
      setShowIFrame(false);
      refreshOneIdCheckStatus();
      if (!hasOneIdV1) {
        pollFallbackEligibility();
      }
      return;
    }

    if (trustmaticResponse?.hasCompleted) {
      const waitTimeToCloseIFrame = hasOneIdV1 ? 0 : 1000;
      if (!hasOneIdV1) {
        pollFallbackEligibility();
        localStorage.setItem(
          ONE_ID_COMPLETION_TIMESTAMP_KEY,
          new Date().toISOString()
        );
        setTimeout(callFallbackTimeout, fallbackPollTimeout);
      }
      if (applicantPortalLogTrustmaticResponseTimes) {
        handleCloseIFrame();
      }
      // give the pollFallbackEligibility time to run
      setTimeout(() => setShowIFrame(false), waitTimeToCloseIFrame);
    }

    if (trustmaticResponse?.hasError) {
      handleTrustmaticErrorEvent(trustmaticResponse);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trustmaticResponse, t]);

  const changeBiometricIdOptOut = useBiometricIdOptOutHandler();

  const refreshSchemaOnFallback = async () => {
    await fetcher.request<components["schemas"]["CaseDetailResponse"]>(
      `/api/internal/applicant/case/${caseId}/`
    );
    setShowNonBiometricIdSection(true);
  };

  const showNonBioContent = async () => {
    await changeBiometricIdOptOut(true);
    setShowNonBiometricIdSection(true);
  };

  const showNonBioLink =
    oneIdAlternativesEligibility?.fallback_type === "NON_BIOMETRIC_CANADA" &&
    fallbackEligibility?.fallback !== "BIO_IDV_OPT_OUT_NOT_ALLOWED" &&
    fallbackEligibility?.fallback !== "PENDING";

  const showHatchSection =
    hasNotSubmittedOneIdOrOptedOut || (hasOneIdV1 && !props.data);

  const showNonBioBanner =
    showHatchSection &&
    showNonBioLink &&
    !fallbackEligibilityIsLoading &&
    !hasOneIdV1 &&
    !disableOptOut;

  const showSkipConsentBanner =
    showHatchSection &&
    !showNonBioLink &&
    !hasOneIdV1 &&
    !optedOutOfBiometricId &&
    !disableOptOut &&
    !!(oneIdAlternativesEligibility?.fallback_type || enableOneIdOptOut);

  const alternativeMethodBanner = (actions: ReactNode) => (
    <SectionMessage
      type="default"
      testID="alternative-verification-banner"
      sectionTitle={t("Don’t have the required verification documents?")}
      actions={actions}
    >
      {t("Use an alternative verification method.")}
    </SectionMessage>
  );

  if (
    (awaitingDemoCompletion && !oneIdV1Complete) ||
    awaitingDemoSessionWrite ||
    hasSubmittedWaitingForOneIdResponse ||
    fallbackEligibility?.fallback === "BIO_IDV_OPT_OUT_NOT_ALLOWED" ||
    (sessionTerminatedByErrors &&
      !(oneIdSessionComplete && !waitForDemoResponse))
  ) {
    return <OneIDLoader />;
  }

  return (
    <>
      <Stack gap="space-2">
        <div className={styles.heading}>
          <Text
            as="h1"
            style="form/page-title"
            testID="identity-verification-section-title"
          >
            {label}
          </Text>

          {subLabel && (
            <Text
              style="form/body-primary"
              color="text-weak"
              testID="identity-verification-section-subtitle"
            >
              {oneIdSessionComplete && !waitForDemoResponse
                ? fallbackEligibility?.fallback ===
                    "FALLBACK_TO_SECONDARY_ID" || sessionTerminatedByErrors
                  ? t(
                      "We failed to verify your identity using that method. Please continue to the next step."
                    )
                  : t(
                      "Your verification details have been submitted. Please continue to the next step."
                    )
                : subLabel}
            </Text>
          )}
        </div>
        <div className={styles.image}>
          {oneIdSessionComplete && !waitForDemoResponse ? (
            <Illustration
              name={
                fallbackEligibility?.fallback === "FALLBACK_TO_SECONDARY_ID" ||
                sessionTerminatedByErrors
                  ? "oneid-fail"
                  : "oneid-success"
              }
              size={132}
            />
          ) : (
            <OneIdImage />
          )}
        </div>
      </Stack>
      <OneIdIFrameModal
        show={showIFrame}
        onClose={closeTrustmaticModal}
        trustmaticUrl={trustmaticUrl}
        isError={trustmaticUrlError}
      />

      <ConfirmCloseTrustmaticModal
        show={openConfirmCloseTrustmaticModal}
        onClose={returnToTrustmaticModal}
        onExit={confirmCloseTrustmaticModal}
      />
      {showHatchSection && !sessionTerminatedByErrors && (
        <>
          <Text
            style="form/body-secondary"
            color="text-weak"
            testID="biometric-notice-statement"
          >
            {applicantPortalBiometricConsentModal ? (
              <Trans>
                Identity verification requires capturing a photo of your
                government ID and a selfie to confirm your identity. Certn uses
                biometric verification to compare these images and confirm your
                identity. Before starting, you'll be asked to review and consent
                to the{" "}
                <BiometricNoticeModal
                  linkName={t("Biometric Notice")}
                  modalHeader={t("Biometric Notice")}
                  contractId={contractId}
                />
                , which explains how Certn collects and uses biometric data.
              </Trans>
            ) : (
              <Trans>
                By clicking "Start verification" below, you consent to the
                processing of your biometric data for this purpose, as further
                explained in our{" "}
                <BiometricNoticeModal
                  linkName={t("Biometric Notice")}
                  modalHeader={t("Biometric Notice")}
                  contractId={contractId}
                />
                .
              </Trans>
            )}
          </Text>

          <Text
            style="form/body-secondary"
            color="text-weak"
            testID="privacy-policy-statement"
          >
            {applicantPortalBiometricConsentModal ? (
              <Trans>
                You can also review Certn's{" "}
                <Link
                  linkStyle="monochrome"
                  testID="privacy-policy-link"
                  href={privacyPolicyUrl}
                  target="_blank"
                >
                  Privacy Policy
                </Link>{" "}
                for more information about how personal information is handled.
              </Trans>
            ) : (
              <Trans>
                Please see our{" "}
                <Link
                  linkStyle="monochrome"
                  testID="privacy-policy-link"
                  href={privacyPolicyUrl}
                  target="_blank"
                >
                  privacy policy
                </Link>{" "}
                for more information about Certn's privacy practices and your
                privacy rights.{" "}
              </Trans>
            )}
          </Text>
        </>
      )}
      <div className={css(styles.actionBar)}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              testID="section-back-button"
              leftIcon="chevron_left"
              onPress={async () => {
                navigateToSection(prevSection);
              }}
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />
          {(oneIdSessionComplete && !waitForDemoResponse) ||
          optedOutOfBiometricId ? (
            fallbackEligibility?.fallback === "FALLBACK_TO_SECONDARY_ID" ? (
              <PrimaryButton
                isDisabled={fallbackEligibilityIsLoading}
                variant="brand-primary"
                testID="id-verification-next-step-to-interac-button"
                onPress={() => {
                  refreshSchemaOnFallback();
                }}
              >
                {t("Next step")}
              </PrimaryButton>
            ) : (
              <PrimaryButton
                testID="section-next-step-button"
                type="submit"
                variant="brand-primary"
                isDisabled={fallbackEligibilityIsLoading}
              >
                {t("Next step")}
              </PrimaryButton>
            )
          ) : (
            <>
              {Constants.enableDemoResponses && !isTrustmaticUrlLoading && (
                <BasicButton
                  onPress={() => {
                    setShowDemoSkipModal(true);
                    setWaitForDemoResponse(true);
                  }}
                >
                  {t("Demo Response")}
                </BasicButton>
              )}

              {allowRealOneIdResponses && (
                <PrimaryButton
                  onPress={() => {
                    if (
                      skipConsentModal ||
                      (!applicantPortalBiometricConsentModal &&
                        !superdocEnabled)
                    ) {
                      if (nfcFlowEnabled) {
                        setShowNfcFlow(true);
                      } else {
                        openVerification();
                      }
                    } else {
                      setShowConsentModal(true);
                    }
                  }}
                  variant="brand-primary"
                  isDisabled={!oneIdSessionId || fallbackEligibilityIsLoading}
                  isLoading={!oneIdSessionId}
                >
                  {t("Start Verification")}
                </PrimaryButton>
              )}
            </>
          )}
        </ButtonGroup>
      </div>
      {showNonBioBanner &&
        alternativeMethodBanner(
          <BasicButton
            testID="use-alternative-verification-button"
            onPress={showNonBioContent}
          >
            {t("Use alternative")}
          </BasicButton>
        )}
      {showSkipConsentBanner &&
        alternativeMethodBanner(
          <ModalTrigger
            trigger={() => (
              <BasicButton testID="use-alternative-verification-button">
                {t("Use alternative")}
              </BasicButton>
            )}
          >
            <Modal>
              <SkipBiometricConsent
                skipEligibility={oneIdAlternativesEligibility?.fallback_type}
              />
            </Modal>
          </ModalTrigger>
        )}
      {Constants.enableDemoResponses && !isTrustmaticUrlLoading && (
        <DemoSkipModal
          isOpen={showDemoSkipModal}
          onClose={() => {
            setShowDemoSkipModal(false);
            setWaitForDemoResponse(false);
          }}
          onSave={async () => {
            if (hasOneIdV1) {
              // Completion is one-id-check-status, not the session id write.
              // Poll until the mocked webhook lands so the first confirm sticks.
              setAwaitingDemoCompletion(true);
              setAwaitingDemoSessionWrite(true);
              setWaitForDemoResponse(false);
              setShowDemoSkipModal(false);
              refreshOneIdCheckStatus();
            } else {
              await pollFallbackEligibility();
            }
            setTimeout(() => {
              handleChange(
                "one_id_session_id",
                "00000000-0000-0000-0000-000000000000"
              );
              if (hasOneIdV1) {
                setAwaitingDemoSessionWrite(false);
              } else {
                setWaitForDemoResponse(false);
                setShowDemoSkipModal(false);
              }
            }, 500); // Second delay to avoid race condition - auto rejected claim updates
          }}
        />
      )}
      <NfcIdVerificationFlow
        isOpen={showNfcFlow}
        onClose={() => setShowNfcFlow(false)}
        onFallbackToIframe={() => {
          setShowNfcFlow(false);
          openVerification();
        }}
        trustmaticUrl={trustmaticUrl}
        isTrustmaticUrlLoading={isTrustmaticUrlLoading}
        trustmaticUrlError={trustmaticUrlError}
        onStartNfcSession={async () => {
          setStartPollingOneIdStatus(true);
          const success = superdocEsignAvailable ? true : await signAgreement();
          if (success) {
            localStorage.setItem(ONE_ID_COMPLETION_TIMESTAMP_KEY, "");
          }
          return success;
        }}
        onNfcSessionComplete={() => {
          handleChange("one_id_session_id", oneIdSessionId);
          if (!hasOneIdV1) {
            pollFallbackEligibility();
            localStorage.setItem(
              ONE_ID_COMPLETION_TIMESTAMP_KEY,
              new Date().toISOString()
            );
            setTimeout(callFallbackTimeout, fallbackPollTimeout);
          }
          setShowNfcFlow(false);
        }}
      />
      <BiometricConsentModal
        isOpen={showConsentModal}
        contractId={contractId}
        onConsent={async () => {
          if (nfcFlowEnabled) {
            setConsentedThisSession(true);
            setShowConsentModal(false);
            setShowNfcFlow(true);
          } else {
            const success = await openVerification();
            if (success) {
              setConsentedThisSession(true);
              setShowConsentModal(false);
            }
          }
        }}
        onCancel={() => setShowConsentModal(false)}
      />
    </>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
    borderTop: "1px solid var(--border-base)",
  }),
  image: css({
    display: "flex",
    justifyContent: "center",
  }),
};
